import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const exploreLinks = [
  { to: '/about',         label: 'About Us' },
  { to: '/leadership',    label: 'Leadership' },
  { to: '/destinations',  label: 'Destinations' },
  { to: '/blog',          label: 'Blog' },
  { to: '/travel-alerts', label: 'Travel Alerts' },
  { to: '/faq',           label: 'FAQ' },
  { to: '/offices',       label: 'Our Offices' },
  { to: '/contact',       label: 'Contact Us' }
];

const serviceLinks = [
  { to: '/services/flight-booking',            label: 'Flight Booking' },
  { to: '/services/hotel-booking',             label: 'Hotel Booking' },
  { to: '/services/visa-assistance',           label: 'Visa Assistance' },
  { to: '/services/holiday-packages',          label: 'Holiday Packages' },
  { to: '/services/travel-insurance',          label: 'Travel Insurance' },
  { to: '/services/cruise-packages',           label: 'Cruise Packages' },
  { to: '/services/airport-transfer',          label: 'Airport Transfer' },
  { to: '/services/corporate-travel-services', label: 'Corporate Travel' }
];

// SVG icons — inline so they work on every device with no emoji rendering issues
const icons = {
  facebook: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
    </svg>
  ),
  instagram: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
    </svg>
  ),
  linkedin: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
      <circle cx="4" cy="4" r="2"/>
    </svg>
  ),
  youtube: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z"/>
      <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/>
    </svg>
  ),
  whatsapp: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
    </svg>
  )
};

const socialLinks = [
  {
    title: 'Facebook',
    bg: '#1877F2',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
      </svg>
    ),
    accounts: [
      { label: 'Bluejay Travels – Dakar', href: 'https://www.facebook.com/profile.php?id=61579770445788' },
      { label: 'Sharda Travel – Abidjan', href: 'https://www.facebook.com/people/Shardatravelabj/61562710124378/' },
      { label: 'Bluejay Travels – Jaipur', href: 'https://www.facebook.com/profile.php?id=61562284517939' },
      { label: 'Bluejay Travels – Conakry', href: 'https://www.facebook.com/profile.php?id=61562289134790' }
    ]
  },
  {
    title: 'Instagram',
    bg: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <circle cx="12" cy="12" r="4"/>
        <circle cx="17.5" cy="6.5" r="1.2" fill="white" stroke="none"/>
      </svg>
    ),
    accounts: [
      { label: 'Bluejay Travel – Dakar', href: 'https://www.instagram.com/bluejaydkr/' },
      { label: 'Sharda Travel – Abidjan', href: 'https://www.instagram.com/shardatravelabj/' },
      { label: 'Bluejay Travel – Jaipur', href: 'https://www.instagram.com/bluejaytravelsjaipur/' },
      { label: 'Bluejay Travel – Conakry', href: 'https://www.instagram.com/bluejaycky/' }
    ]
  },
  {
    title: 'LinkedIn',
    bg: '#0A66C2',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z"/>
        <rect x="2" y="9" width="4" height="12"/>
        <circle cx="4" cy="4" r="2"/>
      </svg>
    ),
    accounts: [
      { label: 'Bluejay Travels', href: 'https://www.linkedin.com/company/bluejay-travels-and-tours/' }
    ]
  },
  {
    title: 'YouTube',
    bg: '#FF0000',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 00-1.95 1.96A29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z"/>
        <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#FF0000"/>
      </svg>
    ),
    accounts: [
      { label: 'Bluejay Travels', href: 'https://www.youtube.com/@Bluejaytravels' }
    ]
  },
  {
    title: 'WhatsApp',
    bg: '#25D366',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
        <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.96 7.96 0 01-4.065-1.115l-.291-.173-3.017.897.897-3.017-.173-.291A7.96 7.96 0 014 12c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8z"/>
      </svg>
    ),
    accounts: [
      { label: 'Dakar Office', href: 'https://wa.me/221777278596' },
      { label: 'Abidjan Office', href: 'https://wa.me/2250719887362' },
      { label: 'Conakry Office', href: 'https://wa.me/224669059915' },
      { label: 'Jaipur Office', href: 'https://wa.me/919587532127' }
    ]
  }
];

function SocialIcons() {
  const [openIndex, setOpenIndex] = React.useState(null);
  const timeoutRef = React.useRef(null);

  function handleMouseEnter(i) {
    clearTimeout(timeoutRef.current);
    setOpenIndex(i);
  }

  function handleMouseLeave() {
    timeoutRef.current = setTimeout(function() {
      setOpenIndex(null);
    }, 200);
  }

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {socialLinks.map(function(s, i) {
        var isOpen = openIndex === i;
        return (
          <div
            key={s.title}
            style={{ position: 'relative' }}
            onMouseEnter={function() { handleMouseEnter(i); }}
            onMouseLeave={handleMouseLeave}
          >
            {/* Icon button */}
            <button
              title={s.title}
              style={{
                display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, borderRadius: 8,
                background: s.bg,
                border: 'none', cursor: 'pointer',
                color: '#fff', flexShrink: 0,
                boxShadow: isOpen
                  ? '0 6px 20px rgba(0,0,0,0.35)'
                  : '0 2px 8px rgba(0,0,0,0.25)',
                transform: isOpen ? 'translateY(-3px)' : 'translateY(0)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
            >
              {s.icon}
            </button>

            {/* Dropdown */}
            <div style={{
              position: 'absolute',
              bottom: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 8px 32px rgba(17,45,78,0.18)',
              padding: '6px 0',
              minWidth: 180,
              zIndex: 200,
              opacity: isOpen ? 1 : 0,
              pointerEvents: isOpen ? 'all' : 'none',
              transition: 'opacity 0.2s ease',
              whiteSpace: 'nowrap'
            }}>
              {/* Arrow pointing down */}
              <div style={{
                position: 'absolute',
                bottom: -6, left: '50%',
                transform: 'translateX(-50%)',
                width: 12, height: 6,
                overflow: 'hidden'
              }}>
                <div style={{
                  width: 10, height: 10,
                  background: '#fff',
                  transform: 'rotate(45deg)',
                  margin: '-5px auto 0',
                  boxShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                }} />
              </div>

              {/* Platform header */}
              <div style={{
                padding: '6px 14px 8px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', gap: 8
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, borderRadius: 5,
                  background: s.bg, flexShrink: 0
                }}>
                  {s.icon}
                </span>
                <span style={{
                  fontSize: '0.75rem', fontWeight: 800,
                  color: 'var(--bj-espresso)',
                  letterSpacing: '0.06em', textTransform: 'uppercase'
                }}>
                  {s.title}
                </span>
              </div>

              {/* Account links */}
              {s.accounts.map(function(acc) {
                return (
                  <a
                    key={acc.href}
                    href={acc.href}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '9px 14px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: 'var(--bj-espresso)',
                      textDecoration: 'none',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = 'var(--bj-cream-soft)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: s.bg instanceof String || typeof s.bg === 'string' && !s.bg.includes('gradient')
                        ? s.bg : '#3F72AF',
                      flexShrink: 0
                    }} />
                    {acc.label}
                    <span style={{ marginLeft: 'auto', opacity: 0.35, fontSize: '0.75rem' }}>↗</span>
                  </a>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ColHeader({ children }) {
  return (
    <h4 style={{
      color: 'var(--bj-cream)', fontSize: '0.78rem',
      fontFamily: 'var(--font-body)', fontWeight: 700,
      letterSpacing: '0.1em', textTransform: 'uppercase',
      margin: '0 0 14px', opacity: 0.9
    }}>
      {children}
    </h4>
  );
}

function FLink({ to, label, small }) {
  return (
    <li style={{ marginBottom: small ? 7 : 9 }}>
      <Link
        to={to}
        style={{
          opacity: 0.65,
          fontSize: small ? '0.82rem' : '0.87rem',
          lineHeight: 1,
          transition: 'opacity 0.15s',
          display: 'block'
        }}
        onMouseEnter={function(e) { e.currentTarget.style.opacity = 1; }}
        onMouseLeave={function(e) { e.currentTarget.style.opacity = 0.65; }}
      >
        {label}
      </Link>
    </li>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  var year = new Date().getFullYear();

  return (
    <footer style={{ background: 'var(--bj-espresso)', color: 'var(--bj-cream)', flexShrink: 0 }}>
      <div className="container" style={{ paddingTop: 52 }}>

        {/* ── MAIN GRID ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr 1fr',
          gap: 40,
          paddingBottom: 36,
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }} className="footer-main-grid">

          {/* Brand */}
          <div className="footer-brand">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <Link to="/">
                <img src="/logo.png" alt="Bluejay Travels" style={{ height: 52, width: 'auto', maxWidth: 160 }} />
              </Link>
              <img
                src="/iata-logo.jpg"
                alt="IATA Accredited Agent"
                style={{
                  height: 44,
                  width: 'auto',
                  objectFit: 'contain',
                  background: '#fff',
                  borderRadius: 8,
                  padding: '5px 10px'
                }}
              />
            </div>
            <p style={{ opacity: 0.65, fontSize: '0.88rem', lineHeight: 1.75, marginBottom: 18, maxWidth: 280 }}>
              A distinguished brand within Sharda Group — delivering extraordinary travel experiences across West Africa and South Asia.
            </p>
            <SocialIcons />
          </div>

          {/* Explore */}
          <div>
            <ColHeader>Explore</ColHeader>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {exploreLinks.map(function(l) { return <FLink key={l.to} to={l.to} label={l.label} />; })}
            </ul>
          </div>

          {/* Services */}
          <div>
            <ColHeader>Services</ColHeader>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {serviceLinks.map(function(l) { return <FLink key={l.to} to={l.to} label={l.label} />; })}
            </ul>
          </div>
        </div>

        {/* ── CONTACT + SUBSIDIARY STRIP ── */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 0',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexWrap: 'wrap', gap: 12
        }} className="footer-contact-strip">
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'row', gap: 12 }}>
                <p style={{ opacity: 0.5, fontSize: '0.75rem', margin: 0, letterSpacing: '0.04em' }}>
                  A subsidiary of
                </p>
                <img
                  src="/sharda-logo.png"
                  alt="Sharda Travel"
                  style={{ height: 48, width: 'auto', maxWidth: 160, objectFit: 'contain' }}
                />
              </div>
        </div>

        {/* ── BOTTOM BAR ── */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '14px 0', fontSize: '0.78rem', opacity: 0.45,
          flexWrap: 'wrap', gap: 8
        }}>
          <span>© {year} Bluejay Travels. {t('footer_rights')}</span>
          <Link to="/admin/login" style={{ opacity: 1, color: 'var(--bj-cream)' }}>Staff Login</Link>
        </div>
      </div>

      <style>{`
        /* ── Tablet: 2-col (brand full width, explore + services side by side) ── */
        @media (max-width: 900px) {
          .footer-main-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 28px !important;
          }
          .footer-brand {
            grid-column: 1 / -1;
            display: flex !important;
            align-items: flex-start !important;
            gap: 24px !important;
            padding-bottom: 20px !important;
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          }
          .footer-brand img { margin-bottom: 0 !important; flex-shrink: 0; }
          .footer-brand p { margin-bottom: 10px !important; }
        }

        /* ── Mobile: compact single brand row, links side by side ── */
        @media (max-width: 600px) {
          .footer-main-grid {
            grid-template-columns: 1fr 1fr !important;
            gap: 0 !important;
            padding-bottom: 0 !important;
            border-bottom: none !important;
          }

          /* Brand spans full width, compact */
          .footer-brand {
            grid-column: 1 / -1 !important;
            flex-direction: row !important;
            align-items: center !important;
            gap: 16px !important;
            padding: 22px 0 18px !important;
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          }
          .footer-brand img {
            height: 38px !important;
            margin-bottom: 0 !important;
            flex-shrink: 0 !important;
          }
          /* Hide tagline on mobile — keeps brand row tight */
          .footer-brand p { display: none !important; }

          /* Explore — left column */
          .footer-main-grid > div:nth-child(2) {
            padding: 20px 16px 16px 0;
            border-right: 1px solid rgba(255,255,255,0.08);
          }

          /* Services — right column */
          .footer-main-grid > div:nth-child(3) {
            padding: 20px 0 16px 16px;
          }

          /* Smaller link text on mobile */
          .footer-main-grid > div:nth-child(2) a,
          .footer-main-grid > div:nth-child(3) a {
            font-size: 0.81rem !important;
          }
          .footer-main-grid > div:nth-child(2) li,
          .footer-main-grid > div:nth-child(3) li {
            margin-bottom: 8px !important;
          }

          /* Contact strip — hide phone/email on mobile (space saver) */
          .footer-contact-strip {
            padding: 12px 0 !important;
          }
          .footer-contact-strip a {
            display: none !important;
          }

          /* Bottom bar — make staff login clearly visible */
          .footer-main-grid ~ div ~ div {
            opacity: 1 !important;
            padding: 12px 0 !important;
          }
          .footer-main-grid ~ div ~ div span {
            opacity: 0.5;
            font-size: 0.76rem;
          }
          .footer-main-grid ~ div ~ div a {
            background: rgba(255,255,255,0.1);
            border: 1px solid rgba(255,255,255,0.2);
            padding: 6px 14px;
            border-radius: 6px;
            font-size: 0.8rem;
            font-weight: 600;
            opacity: 1 !important;
          }
        }
      `}</style>
    </footer>
  );
}